// src/App.js
import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Scene from './components/Scene';
import Captures from './components/Captures';
import Projects from './components/Projects';
import SharedScene from './components/SharedScene';
import LinkExpired from './components/LinkExpired';
import usePageViews from './hooks/usePageViews';
import { AuthProvider, useAuth } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import StreamTestHarness from './StreamTestHarness'; // TEMPORARY — see that file's header
import AccountModal from './components/AccountModal';
import GlobalShareModal from './components/GlobalShareModal';
import QualityModal from './components/QualityModal';
import UsersModal from './components/UsersModal';
import WorkspaceMenu from './components/WorkspaceMenu';

// Resets scroll on every route change. Also handles the case where the
// mobile on-screen keyboard is mid-dismiss at navigation time (e.g. right
// after pressing Login): visualViewport.offsetTop may still read 0 at this
// exact synchronous instant even though the native dismiss animation is
// about to start a moment later (it begins on blur, which happens as the
// old page unmounts) — so we can't gate on offsetTop's current value, or
// the listener needed to catch that later shift never gets attached at
// all. Always listen for a grace window after every navigation instead.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (vv.offsetTop === 0) {
        vv.removeEventListener('resize', onResize);
        clearTimeout(cleanup);
      }
    };
    vv.addEventListener('resize', onResize);
    const cleanup = setTimeout(() => vv.removeEventListener('resize', onResize), 1200);
    return () => { vv.removeEventListener('resize', onResize); clearTimeout(cleanup); };
  }, [pathname]);
  return null;
};

// In-app navigation for the non-React parts of the viewer (SceneHandler.js,
// CaptureHandler.js's scene link): moving between scene and captures without
// a page reload keeps fullscreen and avoids reloading the app.
const NavigateBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.__appNavigate = (url) => navigate(url);
    // The top bars' logo (public/html/*-top.html) is a plain href="/" —
    // a full page load, which left fullscreen. Same in-app navigation as
    // above; Cmd/Ctrl/Shift/middle-click still open it as a link.
    const onLogoClick = (e) => {
      const link = e.target.closest?.('a.logo-viewer-top-link');
      if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate('/');
    };
    document.addEventListener('click', onLogoClick);
    return () => {
      window.__appNavigate = null;
      document.removeEventListener('click', onLogoClick);
    };
  }, [navigate]);
  return null;
};

// Runs the hook inside Router context
const PageViewTracker = () => {
  usePageViews();
  return null;
};

// Listens for logout events dispatched by the HTML UI
const LogoutListener = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handle = async () => {
      await logout();
      navigate('/login');
    };
    window.addEventListener('dashboardLogout', handle);
    return () => window.removeEventListener('dashboardLogout', handle);
  }, [logout, navigate]);

  return null;
};

const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageViewTracker />
        <NavigateBridge />
        <LogoutListener />
        <AccountModal />
        <GlobalShareModal />
        <QualityModal />
        <UsersModal />
        <WorkspaceMenu />
        <Routes>
          <Route path="/login" element={<Login />} />
<Route path="/upload" element={<ProtectedRoute roles={['sysAdmin']}><LandingPage /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/scene" element={<ProtectedRoute><Scene /></ProtectedRoute>} />
          <Route path="/stream-test" element={<StreamTestHarness />} /> {/* TEMPORARY — see StreamTestHarness.js header */}
          <Route path="/captures" element={<ProtectedRoute><Captures /></ProtectedRoute>} />
          <Route path="/s/:shareId" element={<SharedScene />} />
          <Route path="/link-expired" element={<LinkExpired />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
